import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button, Container, Icon, Table } from "semantic-ui-react";
import "./ExternalOrganizationOnlyViewComponent.css";

const DATE_FORMAT = "YYYY-MM-DD";
const ROWS_PER_PAGE = 5;
const TABLE_HEADERS = [
  { key: "org-unit-id", text: "Unit ID" },
  { key: "org-unit-code", text: "Unit Name" },
  { key: "validity-start-date", text: "Start Date" },
  { key: "validity-end-date", text: "End Date" },
];

/**
 * Formatta una data valida senza esporre valori tecnici come NaN.
 * @param {Date|string|Object} value - Valore data ricevuto dal form o dall'API
 * @returns {string} Data in formato YYYY-MM-DD oppure stringa vuota
 */
export const formatDate = value => {
  if (value === null || value === undefined || value === "") return "";

  const date = moment(value);
  return date.isValid() ? date.format(DATE_FORMAT) : "";
};

/**
 * Visualizza la cronologia paginata delle organization unit.
 *
 * Props:
 *   selectedOrgUnits  {Array}  Organization unit persistite da mostrare
 */
const ExternalOrganizationOnlyViewComponent = ({ selectedOrgUnits }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageCount = Math.max(
    1,
    Math.ceil(selectedOrgUnits.length / ROWS_PER_PAGE)
  );
  const indexOfLastRow = currentPage * ROWS_PER_PAGE;
  const indexOfFirstRow = indexOfLastRow - ROWS_PER_PAGE;
  const currentRows = selectedOrgUnits.slice(
    indexOfFirstRow,
    indexOfLastRow
  );

  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [currentPage, pageCount]);

  return (
    <Container className="external-org-unit-table-container">
      <nav
        className="external-org-unit-pagination"
        aria-label="Organization unit table pagination"
      >
        <Button
          aria-label="Previous organization unit page"
          icon
          size="mini"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <Icon aria-hidden="true" name="angle left" />
        </Button>

        <Button
          aria-label="Next organization unit page"
          icon
          size="mini"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage >= pageCount}
        >
          <Icon aria-hidden="true" name="angle right" />
        </Button>
      </nav>

      <Table aria-label="Organization unit history" celled>
        <Table.Header>
          <Table.Row textAlign="center">
            {TABLE_HEADERS.map(item => (
              <Table.HeaderCell key={item.key}>{item.text}</Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {currentRows.length > 0 ? (
            currentRows.map((value, index) => {
              const startDate = formatDate(value.validity_start_date);
              const rowKey = `${value.org_unit_id || "org-unit"}-${
                startDate || indexOfFirstRow + index
              }`;

              return (
                <Table.Row key={rowKey}>
                  <Table.Cell>{value.org_unit_id}</Table.Cell>
                  <Table.Cell>{value.org_unit_code}</Table.Cell>
                  <Table.Cell>{startDate}</Table.Cell>
                  <Table.Cell>
                    {formatDate(value.validity_end_date)}
                  </Table.Cell>
                </Table.Row>
              );
            })
          ) : (
            <Table.Row>
              <Table.Cell colSpan="4" textAlign="center">
                No data available
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Container>
  );
};

const dateValuePropType = PropTypes.oneOfType([
  PropTypes.instanceOf(Date),
  PropTypes.string,
  PropTypes.object,
]);

ExternalOrganizationOnlyViewComponent.propTypes = {
  selectedOrgUnits: PropTypes.arrayOf(
    PropTypes.shape({
      org_unit_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      org_unit_code: PropTypes.string,
      validity_start_date: dateValuePropType,
      validity_end_date: dateValuePropType,
    })
  ).isRequired,
};

export default ExternalOrganizationOnlyViewComponent;
